/**
 * Map Utilities
 *
 * This module contains general utility functions for map operations in the Interactive Survey Market Map.
 * It provides coordinate conversion, map centering, and style management functions.
 */

import { LeaseComp } from "@/features/leaseComps/types";
import type { SurveyPolygon } from "@/features/polygon/types";
import { PortFeature, Property } from "@/features/survey/types";
import getEmeraldBingTiles from "@/services/bingKeyService";
import { MapStyle } from "@/shared/utils/constants";
import mapboxgl from "mapbox-gl";
import { MutableRefObject } from "react";
import { getBottomBarHeight } from "./map";

// Loading state interface
interface LoadingState {
  map: boolean;
  properties: boolean;
  error: string | null;
}

/**
 * Safely converts property coordinates to numbers
 */
export const getCoordinates = (property: Property): [number, number] => {
  const longitude =
    typeof property.longitude === "string"
      ? parseFloat(property.longitude)
      : property.longitude;
  const latitude =
    typeof property.latitude === "string"
      ? parseFloat(property.latitude)
      : property.latitude;

  return [longitude, latitude];
};

/**
 * Retrieves PDF coordinates from property bamMapData if available
 * Checks for pdfCoordinates within the property.bamMapData and returns longitude and latitude
 */
export const getPDFCoordinates = (property: Property): [number, number] => {
  // Check if property has bamMapData as a string
  if (property.bamMapData && typeof property.bamMapData === "string") {
    try {
      // Parse the JSON string
      const bamMapData = JSON.parse(property.bamMapData);

      // Check if the parsed data has pdfCoordinates
      if (bamMapData?.pdfCoordinates) {
        const { longitude, latitude } = bamMapData.pdfCoordinates;

        // Safely convert to numbers if they exist
        if (longitude !== undefined && latitude !== undefined) {
          const lng =
            typeof longitude === "string" ? parseFloat(longitude) : longitude;
          const lat =
            typeof latitude === "string" ? parseFloat(latitude) : latitude;

          // Validate that the parsed values are valid numbers
          if (!isNaN(lng) && !isNaN(lat)) {
            return [lng, lat];
          }
        }
      }
    } catch (error) {
      // If JSON parsing fails, log the error and fall back to standard coordinates
      console.warn("Failed to parse bamMapData JSON:", error);
    }
  }

  // Fallback to standard property coordinates
  return getCoordinates(property);
};

const getPolygonCoordinates = (
  polygons: SurveyPolygon[],
): [number, number][] =>
  polygons
    .filter((polygon) => !polygon.isHidden)
    .flatMap((polygon) => polygon.coordinates)
    .flatMap((ring) => ring)
    .filter(
      (coordinate): coordinate is [number, number] =>
        coordinate.length >= 2 && coordinate.every(Number.isFinite),
    );

/**
 * Centers the map on the filtered properties and/or lease comps
 */
export const centerMap = (
  map: mapboxgl.Map | null,
  filteredProperties: Property[],
  includeMapKey: boolean,
  leaseComps: LeaseComp[] = [],
  polygons: SurveyPolygon[] = [],
): void => {
  const leaseCompCoords: [number, number][] = leaseComps.map((lc) => [
    parseFloat(lc.leaseCompFields?.longitude!),
    parseFloat(lc.leaseCompFields?.latitude!),
  ]);

  const allPoints = [
    ...filteredProperties.map(getCoordinates),
    ...leaseCompCoords,
    ...getPolygonCoordinates(polygons),
  ];

  if (!map || allPoints.length === 0) return;

  if (allPoints.length === 1) {
    map.flyTo({
      center: allPoints[0],
      zoom: 18,
      duration: 1000,
    });
  } else {
    const bounds = new mapboxgl.LngLatBounds();
    const bottomBarHeight = getBottomBarHeight();
    allPoints.forEach((coord) => {
      coord?.length && coord.every((c) => !isNaN(c)) && bounds.extend(coord);
    });
    map.fitBounds(bounds, {
      padding: {
        top: 60,
        left: 60,
        right: 60,
        bottom: includeMapKey ? bottomBarHeight + 50 : 60,
      },
      duration: 1000,
    });
  }
};

/**
 * Changes the map style while preserving current position and zoom
 */
export const changeMapStyle = (
  map: mapboxgl.Map | null,
  style: MapStyle,
  currentMapStyle: MapStyle,
  setLoadingState: (updater: (prev: LoadingState) => LoadingState) => void,
  clearMarkers: () => void,
  clearAllRadiusCircles: () => void,
  addMarkersToMap: () => void,
): void => {
  if (!map || style.id === currentMapStyle.id) {
    return;
  }

  // Preserve current map position and zoom
  const currentCenter = map.getCenter();
  const currentZoom = map.getZoom();
  const currentBearing = map.getBearing();
  const currentPitch = map.getPitch();

  setLoadingState((prev: LoadingState) => ({ ...prev, map: true }));

  // Clear existing markers before style change
  try {
    clearMarkers();
  } catch (error) {
    console.warn("Error clearing markers:", error);
  }

  try {
    clearAllRadiusCircles(); // Clear radius circles as they won't survive style changes
  } catch (error) {
    console.warn("Error clearing radius circles:", error);
  }

  // Add error handler BEFORE setting style to catch any immediate errors
  const errorHandler = (e: mapboxgl.ErrorEvent) => {
    console.error("Map style loading error:", e);
    console.error("Error details:", {
      type: e.type,
      target: e.target,
      error: e.error,
      style: style.name,
      styleUrl: style.url,
    });
    setLoadingState((prev: LoadingState) => ({
      ...prev,
      map: false,
      error: `Failed to load map style: ${style.name} - ${e.error?.message || "Unknown error"}`,
    }));
  };

  map.once("error", errorHandler);

  if (style.name === "Bing Hybrid") {
    // Special handling for emeraldBingHybrid style if needed
    getEmeraldBingTiles().then((bingStyle) => {
      map.setStyle(bingStyle as mapboxgl.StyleSpecification);
    });
  } else {
    try {
      map.setStyle(style.url);
    } catch (immediateError) {
      console.error("Immediate style setting error:", immediateError);
      map.off("error", errorHandler);
      setLoadingState((prev: LoadingState) => ({
        ...prev,
        map: false,
        error: `Failed to set map style: ${immediateError instanceof Error ? immediateError.message : "Unknown error"}`,
      }));
      return;
    }
  }

  // Use once to ensure we only handle the styledata event once per style change
  map.once("styledata", () => {
    // Remove the error handler since style loaded successfully
    map.off("error", errorHandler);

    // Restore the previous map position and zoom after style loads
    if (map) {
      try {
        map.setCenter(currentCenter);
        map.setZoom(currentZoom);
        map.setBearing(currentBearing);
        map.setPitch(currentPitch);
      } catch (error) {
        console.warn(
          "Failed to restore map position after style change:",
          error,
        );
        // Fallback to default behavior if position restoration fails
      }
    }

    setLoadingState((prev: LoadingState) => ({ ...prev, map: false }));

    // Redraw markers after style has loaded and position is restored
    setTimeout(() => {
      try {
        addMarkersToMap();
      } catch (error) {
        console.warn("Error adding markers after style change:", error);
      }
    }, 1000); // Small delay to ensure style and position are fully applied
  });
};

/**
 * Centers the map using mapData with fallback to leaseCompFields for coordinates.
 * Use this for the rendered PDF map view.
 */
export const centerMapWithMapData = (
  map: mapboxgl.Map | null,
  filteredProperties: Property[],
  includeMapKey: boolean,
  leaseComps: LeaseComp[] = [],
  polygons: SurveyPolygon[] = [],
): void => {
  const leaseCompCoords: [number, number][] = leaseComps.map((lc) => [
    parseFloat((lc.mapData?.longitude || lc.leaseCompFields?.longitude)!),
    parseFloat((lc.mapData?.latitude || lc.leaseCompFields?.latitude)!),
  ]);

  const allPoints = [
    ...filteredProperties.map(getCoordinates),
    ...leaseCompCoords,
    ...getPolygonCoordinates(polygons),
  ];

  if (!map || allPoints.length === 0) return;

  if (allPoints.length === 1) {
    map.flyTo({ center: allPoints[0], zoom: 18, duration: 1000 });
  } else {
    const bounds = new mapboxgl.LngLatBounds();
    const bottomBarHeight = getBottomBarHeight();
    allPoints.forEach(
      (coord) =>
        coord?.length && coord.every((c) => !isNaN(c)) && bounds.extend(coord),
    );
    map.fitBounds(bounds, {
      padding: {
        top: 60,
        left: 60,
        right: 60,
        bottom: includeMapKey ? bottomBarHeight + 50 : 60,
      },
      duration: 1000,
    });
  }
};
export const closeCurrentPopup = (
  currentPopupRef: MutableRefObject<mapboxgl.Popup | null>,
): void => {
  if (currentPopupRef.current) {
    currentPopupRef.current.remove();
    currentPopupRef.current = null;
  }
};

export const deleteMarkers = (mapboxComp: mapboxgl.Map) => {
  if (!mapboxComp) return;

  // Check if style is loaded before accessing it
  if (!mapboxComp.isStyleLoaded()) {
    console.warn("Map style not loaded yet, skipping layer/source removal");
  } else {
    // Remove radius ring layers
    const style = mapboxComp.getStyle();
    const radiusLayers =
      style?.layers?.filter((layer) =>
        layer.id.includes("radius-ring-outline"),
      ) ?? [];

    for (const layer of radiusLayers) {
      if (mapboxComp.getLayer(layer.id)) {
        mapboxComp.removeLayer(layer.id);
      }
    }

    // Remove radius ring sources
    const allSources = style?.sources;
    if (allSources) {
      const radiusSources = Object.keys(allSources).filter((sourceId) =>
        sourceId.includes("radius-ring"),
      );

      for (const sourceId of radiusSources) {
        if (mapboxComp.getSource(sourceId)) {
          mapboxComp.removeSource(sourceId);
        }
      }
    }
  }

  // Remove markers (always safe to do)
  const markers = (mapboxComp as unknown as { _markers?: mapboxgl.Marker[] })
    ._markers;
  if (markers) {
    for (const marker of markers) {
      const el = marker.getElement();
      if (el?.classList.contains("polygon-label-marker")) continue;
      el?.remove();
    }
  }
};

/**
 * Sanitizes HTML content to prevent XSS attacks
 * Follows security best practices with input validation
 */
export const sanitizeHtml = (input: string): string => {
  if (typeof input !== "string") {
    return "";
  }

  // Create a temporary div element for safe HTML escaping
  const div = document.createElement("div");
  div.textContent = input;
  return div.innerHTML;
};

/**
 * Validates and sanitizes port name input
 * Implements input validation following clean architecture principles
 */
const validatePortName = (portName: unknown): string => {
  if (typeof portName !== "string" || !portName.trim()) {
    return "Unknown Port";
  }

  // Sanitize and limit length to prevent abuse
  const sanitized = sanitizeHtml(portName.trim());
  return sanitized.length > 100
    ? sanitized.substring(0, 100) + "..."
    : sanitized;
};

/**
 * Creates secure popup content using DOM manipulation instead of innerHTML
 * Follows secure coding practices with proper input validation
 */
const createSecurePopupContent = (portName: unknown): HTMLElement => {
  const container = document.createElement("div");
  container.style.fontFamily = "Calibre";
  container.style.fontSize = "12px";
  container.style.color = "#000";
  container.style.display = "flex";
  container.style.padding = "5px 10px";
  container.style.justifyContent = "center";
  container.style.alignItems = "center";

  const strongElement = document.createElement("div");
  strongElement.style.color = "gray";

  // Use textContent instead of innerHTML for security
  strongElement.textContent = validatePortName(portName);

  container.appendChild(strongElement);

  return container;
};

export const addMarinePortLayer = (
  map: mapboxgl.Map,
  marinePorts: PortFeature[],
  portsVisibility: boolean,
): void => {
  const sourceId = "marine-port";
  const layerId = "marine-port-layer";

  // Clean up existing layers and sources
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }

  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }

  map.addSource(sourceId, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: marinePorts.map((eachPort: PortFeature) => {
        const {
          geometry: { coordinates },
          properties: { PORT_NAME },
        } = eachPort;

        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates,
          },
          properties: {
            portName: validatePortName(PORT_NAME), // Pre-sanitize the data
          },
        };
      }),
    },
  });

  map.addLayer({
    id: layerId,
    type: "circle",
    source: sourceId,
    paint: {
      "circle-radius": 4,
      "circle-color": "yellow",
      "circle-stroke-width": 1,
      "circle-stroke-color": "#999999",
    },
  });

  if (!portsVisibility) {
    map.setLayoutProperty(layerId, "visibility", "none");
  }

  let popup: mapboxgl.Popup | null = null;

  map.on("mouseenter", layerId, (e) => {
    map.getCanvas().style.cursor = "pointer";

    if (e?.features && e.features.length > 0) {
      const { geometry, properties } = e.features[0];
      let coordinates: [number, number] | undefined;

      if (geometry.type === "Point" && geometry.coordinates.length === 2) {
        coordinates = geometry.coordinates as [number, number];
      }

      if (coordinates && properties) {
        const { portName } = properties as { portName: string };

        // Create secure popup content using DOM manipulation
        const popupContent = createSecurePopupContent(portName);

        popup = new mapboxgl.Popup({
          closeButton: false,
        })
          .setLngLat(coordinates)
          .setDOMContent(popupContent) // Use setDOMContent instead of setHTML
          .addTo(map);
      }
    }
  });

  map.on("mouseleave", layerId, () => {
    const canvas = map.getCanvas();
    canvas.style.cursor = "";

    if (popup) {
      popup.remove();
      popup = null;
    }
  });
};
